/**
 * Domain helpers shared by the poster components and the library feature:
 * progress math and the status → words mapping used for row meta lines and
 * accessible labels (mirrors the app's LibraryTile a11y label).
 */

import type { LibraryItem, UserShowStatus } from '@shared/types';

import { localized } from '@/i18n/localized';
import { tForLocale, type Locale } from '@/i18n/locale';

/** The user-facing noun for each non-removed status (screens.md §`/`). */
export const STATUS_NOUN_KEY: Record<Exclude<UserShowStatus, 'removed'>, string> = {
  watchlist: 'library.status.watchlist',
  watching: 'library.status.watching',
  completed: 'library.status.completed',
  dropped: 'library.status.dropped',
};

/**
 * 0–100 progress for a library row. Prefers the server's `progress_percent`,
 * derives from episode counts, and treats completed as done.
 */
export function progressPercent(item: LibraryItem): number {
  const fromServer = item.title.progressPercent;
  if (typeof fromServer === 'number' && Number.isFinite(fromServer)) return fromServer;
  if (item.totalEpisodes > 0) return (item.watchedCount / item.totalEpisodes) * 100;
  return item.status === 'completed' ? 100 : 0;
}

/** "Watching · 5%", "Completed · Rated 5", "Watchlist" — no title, no status for removed. */
export function libraryStatusLine(item: LibraryItem, locale: Locale = 'en'): string {
  if (item.status === 'removed') return '';
  const parts: string[] = [tForLocale(locale, STATUS_NOUN_KEY[item.status])];
  if (item.status === 'watching') {
    const pct = Math.round(progressPercent(item));
    if (pct > 0) parts.push(`${pct}%`);
  }
  if (item.userRating) parts.push(tForLocale(locale, 'library.status.rated', { rating: item.userRating }));
  return parts.join(' · ');
}

/** Accessible name for a library tile/row: "Breaking Bad, Watching · 5%". */
export function libraryItemLabel(item: LibraryItem, locale: Locale = 'en'): string {
  const title = localized(item.title.title, item.title.titleBr, locale);
  const line = libraryStatusLine(item, locale);
  return line ? `${title}, ${line}` : title;
}
